import { ArrowRight, Graph, LinkSimple } from "@phosphor-icons/react";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useWisdomNode } from "~/api/queries";
import type { WisdomNode } from "~/api/types";
import { Spinner } from "~/foundation/components/Spinner";
import { cn } from "~/foundation/utils/cn";

type Connection = WisdomNode["edges"][number]["returned"][number];
type Group = "causal" | "assessed" | "related";
const CAUSAL = new Set(["CAUSES", "DRIVES", "INFLUENCES", "PREVENTS"]);
const ASSESSED = new Set(["SUPPORTS", "CONTRADICTS", "REFUTES", "CORROBORATES", "VALIDATES"]);
const groupOf = (rel: string): Group =>
  CAUSAL.has(rel.toUpperCase())
    ? "causal"
    : ASSESSED.has(rel.toUpperCase())
      ? "assessed"
      : "related";

/** Only server-returned edges; certainty belongs to the linked source, not to the arrow. */
export function DocumentConnections({ documentId }: { documentId: string }) {
  const { t } = useTranslation();
  const { data, isLoading, isError, refetch } = useWisdomNode("document", documentId);
  const [selection, setSelection] = useState<Group | null>(null);
  const [showAll, setShowAll] = useState(false);
  const groups: Record<Group, Connection[]> = { causal: [], assessed: [], related: [] };
  for (const group of data?.edges ?? []) groups[groupOf(group.rel)].push(...group.returned);
  const selected =
    selection ??
    (groups.causal.length ? "causal" : groups.assessed.length ? "assessed" : "related");
  const rows = groups[selected];
  const withheld = (data?.edges ?? []).reduce((sum, group) => sum + group.withheld, 0);
  const truncated = data?.edges.some((group) => group.truncated);
  const labels = { causal: t("Causality"), assessed: t("Assessed links"), related: t("Related") };
  const empty = {
    causal: t("No causal links available."),
    assessed: t("No assessed links available."),
    related: t("No related links available."),
  };
  const relationLabel = (edge: Connection) => {
    const incoming = edge.direction === "in";
    switch (edge.rel.toUpperCase()) {
      case "CAUSES":
        return incoming ? t("Caused by") : t("Causes");
      case "DRIVES":
        return incoming ? t("Driven by") : t("Drives");
      case "INFLUENCES":
        return incoming ? t("Influenced by") : t("Influences");
      case "PREVENTS":
        return incoming ? t("Prevented by") : t("Prevents");
      case "SUPPORTS":
        return incoming ? t("Supported by") : t("Supports");
      case "CONTRADICTS":
        return incoming ? t("Contradicted by") : t("Contradicts");
      case "REFUTES":
        return incoming ? t("Refuted by") : t("Refutes");
      case "CORROBORATES":
        return incoming ? t("Corroborated by") : t("Corroborates");
      case "VALIDATES":
        return incoming ? t("Validated by") : t("Validates");
      case "DERIVED_FROM":
        return incoming ? t("Source for") : t("Derived from");
      case "REFERENCES":
        return incoming ? t("Referenced by") : t("References");
      case "MENTIONS":
        return incoming ? t("Mentioned by") : t("Mentions");
      case "ABOUT":
        return incoming ? t("Discussed in") : t("About");
      default:
        return `${edge.direction === "in" ? "←" : "→"} ${edge.rel.toLowerCase().replaceAll("_", " ")}`;
    }
  };
  const certaintyLabel = (certainty: string | null) => {
    switch (certainty) {
      case "observed":
        return t("Source: observed");
      case "reported":
        return t("Source: reported");
      case "inferred":
        return t("Source: inferred");
      default:
        return null;
    }
  };

  return (
    <section
      id="document-connections"
      aria-label={t("Connections")}
      className="scroll-mt-6 border-t border-divider pt-5"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-body font-semibold text-text">
          <LinkSimple size={16} aria-hidden className="text-text-tertiary" />
          {t("Connections")}
        </h2>
        <Link
          to="/knowledge/browse"
          search={{ view: "graph", kind: "document", id: documentId }}
          className="inline-flex items-center gap-1.5 text-small text-text-secondary hover:text-link"
        >
          <Graph size={14} aria-hidden />
          {t("Explore graph")}
          <ArrowRight size={12} aria-hidden className="rtl:rotate-180" />
        </Link>
      </div>
      {isLoading ? (
        <div className="py-4">
          <Spinner size={16} label={t("Loading connections")} />
        </div>
      ) : isError ? (
        <div className="flex items-center gap-3 py-4 text-small text-text-tertiary">
          <span>{t("Connections could not be loaded.")}</span>
          <button
            type="button"
            onClick={() => void refetch()}
            className="text-link hover:underline"
          >
            {t("Retry")}
          </button>
        </div>
      ) : (
        <>
          <div
            className="mt-3 flex flex-wrap gap-1"
            role="group"
            aria-label={t("Connection types")}
          >
            {(["causal", "assessed", "related"] as const).map((group) => (
              <button
                key={group}
                type="button"
                aria-pressed={selected === group}
                onClick={() => {
                  setSelection(group);
                  setShowAll(false);
                }}
                className={cn(
                  "flex items-center gap-2 rounded-sm px-2.5 py-1.5 text-small",
                  selected === group
                    ? "bg-bg-secondary font-medium text-text"
                    : "text-text-tertiary hover:bg-hover-bg hover:text-text",
                )}
              >
                {labels[group]}{" "}
                <span className="text-xs tabular-nums text-text-tertiary">
                  {groups[group].length}
                </span>
              </button>
            ))}
          </div>
          {rows.length ? (
            <ul className="mt-2 divide-y divide-divider">
              {(showAll ? rows : rows.slice(0, 4)).map((edge) => {
                const content = (
                  <>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-body text-text">{edge.title}</span>{" "}
                      <span className="text-xs text-text-tertiary">
                        {relationLabel(edge)}
                        {certaintyLabel(edge.certainty)
                          ? ` · ${certaintyLabel(edge.certainty)}`
                          : ""}
                      </span>
                    </span>
                    <ArrowRight
                      size={14}
                      aria-hidden
                      className="shrink-0 text-text-tertiary rtl:rotate-180"
                    />
                  </>
                );
                const className =
                  "flex items-center gap-3 rounded-sm px-2 py-2.5 hover:bg-hover-bg";
                return (
                  <li key={`${edge.rel}:${edge.direction}:${edge.kind}:${edge.id}`}>
                    {edge.kind === "document" ? (
                      <Link
                        to="/documents/$documentId"
                        params={{ documentId: edge.id }}
                        className={className}
                      >
                        {content}
                      </Link>
                    ) : (
                      <Link
                        to="/knowledge/browse"
                        search={{ view: "graph", kind: edge.kind, id: edge.id }}
                        className={className}
                      >
                        {content}
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="px-2 py-4 text-small text-text-tertiary">{empty[selected]}</p>
          )}
          {rows.length > 4 ? (
            <button
              type="button"
              onClick={() => setShowAll(!showAll)}
              className="px-2 py-1 text-small text-link hover:underline"
            >
              {showAll ? t("Show less") : t("Show all links")}
            </button>
          ) : null}
          {withheld > 0 ? (
            <p className="mt-2 text-xs text-text-tertiary">
              {t("{{n}} more, not shown to you", { n: withheld })}
            </p>
          ) : null}
          {truncated ? (
            <p className="mt-2 text-xs text-text-tertiary">
              {t("More connections are available in the graph.")}
            </p>
          ) : null}
        </>
      )}
    </section>
  );
}
